import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { runBootstrap } from '@/features/app-state';
import { BootstrapPhase } from '@/features/app-state/appStateTypes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// Step components (you will create / adapt these)
import { Stepper, type Step } from '@/components/stepper/stepper';
import CreateProfilePage from './create-profile';
import VerifyPhoneStep from './verify-phone';
import OrganizationStep from './organization-step';
import { selectActiveInvitationToken } from '@/features/invitations';
import { getOnboardingStep } from '@/features/users/onboardingSelectors';
import { selectActiveOrganizationId, selectCurrentUserProfile } from '@/store/selectors/authorizationSelectors';

/**
 * Wizard logic is derived from user/profile state.
 * No manual "next" state required (but steps can call navigate('/') on finish).
 */
export default function OnboardingWizardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
  const hasTriggeredCompletionBootstrapRef = useRef(false);

  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const authEmail = useAppSelector((s) => s.auth.email);
  const activeInvitationToken = useAppSelector(selectActiveInvitationToken);
  const bootstrapPhase = useAppSelector((s) => s.appState.bootstrapPhase);
  const isBootstrapping = useAppSelector((s) => s.appState.isBootstrapping);

  const profile = useAppSelector(selectCurrentUserProfile);
  const error = useAppSelector((s) => s.users.error);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const memberships = useAppSelector((s) => {
    const memberId = s.users.profile?.applicationUserId;
    if (!memberId) return [];
    return s.organizations.membershipsByMemberId[memberId] ?? [];
  });

  const userHasActiveMembership = memberships.length > 0;
  const stepKey = getOnboardingStep(profile, userHasActiveMembership);

  // If you don't have phoneVerified yet, keep it as false for now.
  // Add this field in backend + type later.
  // const profileCompleted = !!profile?.profileCompleted;
  // const phoneVerified = !!(profile as IBusinessUser)?.phoneVerified; // TODO: add to UserProfile type properly
  // const hasOrg = !!profile?.organizationID;

  const steps: Step[] = useMemo(
    () =>
      userHasActiveMembership
        ? [
            { key: 'profile', label: 'Create profile' },
            { key: 'phone', label: 'Verify phone' },
          ]
        : [
            { key: 'profile', label: 'Create profile' },
            { key: 'phone', label: 'Verify phone' },
            { key: 'org', label: 'Organization' },
          ],
    [userHasActiveMembership],
  );

  // If onboarding is done, get out of here
  // if (profileCompleted && phoneVerified && (hasOrg || !profile?.allowCreateOrg)) {
  //   // NOTE: if allowCreateOrg is false, they might still be allowed into dashboard
  //   // and handle org creation later via banner. Adjust to your preference.
  //   navigate('/', { replace: true });
  // }

  /**
   * Decide current step based on profile flags.
   * You can tweak this logic anytime without changing the UI structure.
   */
  const requiredIndex = stepKey
    ? Math.max(
        steps.findIndex((step) => step.key === stepKey),
        0,
      )
    : 0;
  const activeIndex = selectedStepIndex === null ? requiredIndex : Math.min(selectedStepIndex, requiredIndex);

  useEffect(() => {
    setSelectedStepIndex(null);
  }, [requiredIndex]);

  useEffect(() => {
    if (stepKey !== null) {
      hasTriggeredCompletionBootstrapRef.current = false;
      return;
    }

    if (bootstrapPhase === BootstrapPhase.Ready) return;
    if (isBootstrapping) return;
    if (hasTriggeredCompletionBootstrapRef.current) return;

    hasTriggeredCompletionBootstrapRef.current = true;
    void dispatch(runBootstrap());
  }, [bootstrapPhase, dispatch, isBootstrapping, stepKey]);

  // Auth guard
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (activeInvitationToken) {
    return <Navigate to="/invite/accept" replace />;
  }

  if (stepKey === null) {
    if (bootstrapPhase !== BootstrapPhase.Ready) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm font-medium">Finishing setup…</p>
              <p className="text-muted-foreground text-sm">Loading your organization access and dashboard.</p>
            </CardContent>
          </Card>
        </div>
      );
    }
    return <Navigate to="/" replace />;
  }

  // Hard error state (non-onboarding)
  if (error && profile) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>We couldn’t load your onboarding state.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-destructive text-sm">{error}</p>
            <Button onClick={() => window.location.reload()} className="w-full">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Finish setting up your account</CardTitle>
            <CardDescription>Complete a few steps to unlock your HomeVein business dashboard.</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <Stepper
              steps={steps}
              activeIndex={activeIndex}
              maxEnabledIndex={requiredIndex}
              onStepClick={setSelectedStepIndex}
            />

            {activeIndex === 0 && (
              <CreateProfilePage
                onSuccess={() => {
                  toast.success('Profile saved.');
                  setSelectedStepIndex(null);
                  // After saving profile, wizard will naturally move to step 2
                  // once profile.profileCompleted becomes true in Redux.
                }}
              />
            )}

            {activeIndex === 1 && (
              <VerifyPhoneStep
                onSuccess={() => {
                  toast.success('Phone verified.');
                  setSelectedStepIndex(null);
                  // Once phoneVerified is true, wizard moves to step 3
                }}
                onSkip={() => {
                  // If you want to allow skipping phone verification, wire it here
                  toast.message('Phone verification skipped.');
                }}
              />
            )}

            {activeIndex === 2 && (
              <OrganizationStep
                allowCreateOrg={!!profile?.allowCreateOrg}
                organizationID={activeOrganizationId ?? undefined}
                profileAddress={profile?.address}
                profileEmail={profile?.email}
                profilePhone={profile?.phone}
                authEmail={authEmail}
                onComplete={() => {
                  toast.success('Organization step complete.');
                  navigate('/', { replace: true });
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
